/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: DialProfilesPage.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Dial Profiles module.
----------------------------------------------------------------*/


import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PhoneCall, Edit, Trash2, Copy } from 'lucide-react';
import { ModulePageContainer } from '../../components/layouts';
import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../hooks/useNotifications';
import { usePermissions, MODULE_SLUGS } from '../../hooks/usePermissions';
import { useDialProfileList } from './hooks';
import { deleteDialProfile } from './api';
import { DialProfile } from './types';
import {
  navigateToDialProfileDuplicate,
} from './dialProfilesActions';

const ALLOW_BY_LABEL_MAP: Record<string, string> = {
  none: 'none',
  caller: 'caller',
  recipient: 'recipient',
  both: 'both',
};

const CALL_SCREENING_MAP: Record<string, string> = {
  disabled: 'disabled',
  ask_once: 'askOnce',
  always_ask: 'alwaysAsk',
};

export default function DialProfilesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();
  const { canWriteModule } = usePermissions();

  const isSettingsContext = location.pathname.startsWith('/settings/technology/dial-profile');
  const basePath = isSettingsContext ? '/settings/technology/dial-profile' : '/pbx/applications/dial-profiles';

  const [deleteState, setDeleteState] = useState<{
    isOpen: boolean;
    profile: DialProfile | null;
    isDeleting: boolean;
  }>({
    isOpen: false,
    profile: null,
    isDeleting: false,
  });


  const columns = useMemo(
    () => [
      {
        key: 'name',
        label: t('dialProfiles.fields.name.label'),
        visible: true,
        sortable: true,
      },
      {
        key: 'allowTransferBy',
        label: t('dialProfiles.fields.allowTransferBy.label'),
        visible: true,
        sortable: true,
        render: (_value: string, item: DialProfile) => {
          const key = ALLOW_BY_LABEL_MAP[item.allowTransferBy?.toLowerCase?.() ?? ''] ?? 'recipient';
          return t(`dialProfiles.fields.allowTransferBy.options.${key}`);
        },
      },
      {
        key: 'allowParkBy',
        label: t('dialProfiles.fields.allowParkBy.label'),
        visible: true,
        sortable: true,
        render: (_value: string, item: DialProfile) => {
          const key = ALLOW_BY_LABEL_MAP[item.allowParkBy?.toLowerCase?.() ?? ''] ?? 'recipient';
          return t(`dialProfiles.fields.allowParkBy.options.${key}`);
        },
      },
      {
        key: 'ringbackTone',
        label: t('dialProfiles.fields.ringbackTone.label'),
        visible: true,
        sortable: true,
      },
      {
        key: 'callScreening',
        label: t('dialProfiles.fields.callScreening.label'),
        visible: true,
        sortable: true,
        render: (_value: string, item: DialProfile) => {
          const key = CALL_SCREENING_MAP[item.callScreening?.toLowerCase?.() ?? 'disabled'] ?? 'disabled';
          return t(`dialProfiles.fields.callScreening.options.${key}`);
        },
      },
      {
        key: 'ringingTone',
        label: t('dialProfiles.fields.ringingTone.label'),
        visible: false,
        sortable: true,
        render: (_value: boolean, item: DialProfile) => (
          <span
            className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${item.ringingTone
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
              }`}
          >
            {item.ringingTone ? t('common.enabled') : t('common.disabled')}
          </span>
        ),
      },
      {
        key: 'active',
        label: t('common.statusText'),
        visible: true,
        sortable: true,
        render: (_value: boolean, item: DialProfile) => (
          <span
            className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${item.active
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
              }`}
          >
            {item.active ? t('common.enabled') : t('common.disabled')}
          </span>
        ),
      },
      {
        key: 'actions',
        label: t('common.actions'),
        visible: true,
        sortable: false,
        width: '120px',
      },
    ],
    [t],
  );

  // Use custom hook for list management
  const {
    data,
    loading,
    columns: gridColumns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useDialProfileList({ initialColumns: columns });

  const handleCreate = useCallback(() => {
    navigate(`${basePath}/new`);
  }, [basePath, navigate]);

  const handleEdit = useCallback(
    (profile: DialProfile) => {
      navigate(`${basePath}/${profile.uuid ?? profile.id}`);
    },
    [basePath, navigate],
  );

  const handleDuplicate = useCallback(
    (profile: DialProfile) => {
      navigateToDialProfileDuplicate(navigate, profile.uuid ?? profile.id, isSettingsContext);
    },
    [navigate, isSettingsContext],
  );

  const handleDeleteClick = useCallback((profile: DialProfile) => {
    setDeleteState({
      isOpen: true,
      profile,
      isDeleting: false,
    });
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setDeleteState({
      isOpen: false,
      profile: null,
      isDeleting: false,
    });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteState.profile) {
      return;
    }

    setDeleteState((prev) => ({ ...prev, isDeleting: true }));

    try {
      await deleteDialProfile(deleteState.profile.id);

      showSuccess(t('dialProfiles.messages.deleteSuccess', { name: deleteState.profile.name }));
      setDeleteState({
        isOpen: false,
        profile: null,
        isDeleting: false,
      });
      refresh();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t('dialProfiles.messages.deleteError');
      showError(message);
      setDeleteState((prev) => ({ ...prev, isDeleting: false }));
    }
  }, [deleteState.profile, refresh, showError, showSuccess, t]);

  const permissionSlugs = useMemo(
    () => [
      MODULE_SLUGS.DIAL_PROFILES,
      'pbx_applications_dial_profile',
      'settings_tech_dial_profile',
    ],
    [],
  );

  const canWrite = useMemo(() => {
    return permissionSlugs.some((slug) => canWriteModule(slug));
  }, [canWriteModule, permissionSlugs]);

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          data={data}
          columns={gridColumns}
          loading={loading}
          pagination={pagination}
          searchQuery={searchQuery}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSearch={handleSearch}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onColumnToggle={handleColumnToggle}
          onAdd={canWrite ? handleCreate : undefined}
          addButtonText={t('common.add')}
          title={t('dialProfiles.title')}
          titleIcon={<PhoneCall className="h-5 w-5 text-primary" />}
          description={t('dialProfiles.description')}
          renderActions={
            canWrite
              ? (item: DialProfile) => (
                <div className="flex items-center justify-end gap-2">
                  <SimpleTooltip content={t('common.edit')} position="top">
                    <button
                      type="button"
                      onClick={() => handleEdit(item)}
                      className="p-2 rounded-md transition-colors hover:bg-blue-100 dark:hover:bg-blue-900/30"
                    >
                      <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      <span className="sr-only">{t('common.edit')}</span>
                    </button>
                  </SimpleTooltip>
                  <SimpleTooltip content={t('common.duplicate')} position="top">
                    <button
                      type="button"
                      onClick={() => handleDuplicate(item)}
                      className="p-2 rounded-md transition-colors hover:bg-emerald-100 dark:hover:bg-emerald-900/30"
                    >
                      <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                      <span className="sr-only">{t('common.duplicate')}</span>
                    </button>
                  </SimpleTooltip>
                  {!item.isDefault && (
                    <SimpleTooltip content={t('common.delete')} position="top">
                      <button
                        type="button"
                        onClick={() => handleDeleteClick(item)}
                        className="p-2 rounded-md transition-colors hover:bg-red-100 dark:hover:bg-red-900/30"
                      >
                        <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                        <span className="sr-only">{t('common.delete')}</span>
                      </button>
                    </SimpleTooltip>
                  )}
                </div>
              )
              : undefined
          }
        />
      }
      formView={null}
    >
      <ConfirmationModal
        isOpen={deleteState.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('dialProfiles.messages.deleteTitle')}
        message={t('dialProfiles.messages.deleteConfirmation', {
          name: deleteState.profile?.name ?? '',
        })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={deleteState.isDeleting}
      />
    </ModulePageContainer>
  );
}


